import React, { useEffect, useState } from 'react';
import { Plane, AlertCircle, Calendar, Briefcase, Utensils, RefreshCcw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fetchAirlineLogos } from '../../utils/airlineLogoCache';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);

const formatChipDate = (yyyyMmDd) => {
  try {
    const d = parseISO(yyyyMmDd);
    return {
      weekday: format(d, 'EEE'),
      day: format(d, 'd'),
      month: format(d, 'MMM'),
      full: format(d, 'EEE, d MMM'),
    };
  } catch {
    return { weekday: '', day: '', month: '', full: yyyyMmDd };
  }
};

/**
 * SpecialDealCard v2 — compact, tail-logo-led card.
 *
 * Improvements over v1:
 *  - Real airline tail logo (via /api/airline-logos cache) replacing the
 *    generic plane glyph
 *  - Horizontal flight-path visual: IATA · ─── stop-pill ─── · IATA
 *  - Inline times row below the route
 *  - Trip-type badge moved to header right of airline name for tighter
 *    vertical rhythm
 *  - Amenities micro-row (refundable, meal, baggage) for credibility
 *  - Lower-key urgency colour ramp (amber border + tinted bg, not screaming red)
 *  - All on existing palette (#1B375B / #659A9B / #F5A623 / #F5FBFB)
 */
const SpecialDealCard = ({ deal, onBook, testId }) => {
  const route = deal.route || {};
  const fromCity = route.from_city || route.from_airport;
  const toCity = route.to_city || route.to_airport;
  const lowestAdult = deal.lowest_fare?.adult || 0;
  const isRound = deal.ticket_type === 'roundtrip';
  const segCount = deal.segments?.length || 0;
  const isVia = deal.flight_type === 'via';
  const stops = isVia ? `${Math.max(segCount - 1, 1)} stop${Math.max(segCount - 1, 1) > 1 ? 's' : ''}` : 'Non-stop';

  const urgent = (deal.soonest_seats || 0) > 0 && (deal.soonest_seats || 0) <= 5;
  const soonest = deal.soonest_date ? formatChipDate(deal.soonest_date) : null;
  const soonestReturn = deal.soonest_return_date ? formatChipDate(deal.soonest_return_date) : null;
  const showChips = (deal.total_upcoming_dates || 0) > 1 && (deal.next_departures || []).length > 1;

  // Occasion ribbon — colour token from the soonest cycle's `occasion_color`
  // mapped to existing brand-palette hexes. NEVER hard-codes label names.
  const occasionLabel = deal.soonest_occasion_label;
  const occasionColor = deal.soonest_occasion_color || 'saffron';
  const RIBBON_HEX = {
    saffron: '#F5A623',
    mint:    '#159C5C',
    navy:    '#1B375B',
    teal:    '#659A9B',
    amber:   '#92400e',
    violet:  '#7C3AED',
  };
  const ribbonBg = RIBBON_HEX[occasionColor] || RIBBON_HEX.saffron;

  // Airline tail logo
  const [logoUrl, setLogoUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!deal.airline_code) return undefined;
    fetchAirlineLogos([deal.airline_code])
      .then((map) => {
        if (cancelled) return;
        const entry = map?.[deal.airline_code];
        if (entry?.has_logo && entry?.best) setLogoUrl(entry.best);
      })
      .catch(() => { /* silent — placeholder stays */ });
    return () => { cancelled = true; };
  }, [deal.airline_code]);

  return (
    <div
      className="group relative bg-white rounded-2xl border border-[#659A9B]/20 hover:border-[#659A9B]/55 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
      data-testid={testId || `deal-card-${deal.id}`}
    >
      {/* Occasion ribbon — dynamic label, dynamic colour from offline_occasion_labels */}
      {occasionLabel && (
        <div
          className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white text-center shadow-inner"
          style={{ background: `linear-gradient(90deg, ${ribbonBg} 0%, ${ribbonBg}dd 100%)` }}
          data-testid={`deal-occasion-ribbon-${deal.id}`}
        >
          {occasionLabel}
        </div>
      )}
      {/* Accent strip */}
      <div className="h-[3px] bg-gradient-to-r from-[#1B375B] via-[#335E77] to-[#659A9B]" />

      <div className="p-4 flex-1 flex flex-col gap-3">
        {/* Header — tail logo + airline + trip type */}
        <div className="flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-xl bg-white border border-[#659A9B]/15 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${deal.airline_name} tail`}
                className="w-full h-full object-contain p-1"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid={`deal-tail-logo-${deal.id}`}
              />
            ) : (
              <Plane className="w-5 h-5 text-[#1B375B]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-[#1B375B] leading-tight truncate">{deal.airline_name}</div>
            <div className="text-[10px] text-gray-500 leading-tight mt-0.5">{deal.flight_number}</div>
          </div>
          <span
            className={`text-[9.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-md whitespace-nowrap flex-shrink-0 ${
              isRound
                ? 'bg-[#1B375B] text-white'
                : 'bg-[#659A9B]/15 text-[#335E77] border border-[#659A9B]/35'
            }`}
            data-testid="deal-trip-type"
          >
            {isRound ? 'Round-trip' : 'One-way'}
          </span>
        </div>

        {/* Flight path — horizontal route + arrow line + stop pill */}
        <div className="flex items-center gap-2">
          <div className="text-left flex-shrink-0">
            <div className="text-lg font-bold text-[#1B375B] tracking-tight leading-none">{route.from_airport}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[90px]">{fromCity}</div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-1">
            <div className="w-full flex items-center gap-1">
              <div className="h-px flex-1 bg-gradient-to-r from-[#659A9B]/15 via-[#659A9B]/40 to-[#659A9B]/15" />
              <Plane className="w-3 h-3 text-[#659A9B] rotate-90 flex-shrink-0" strokeWidth={2.5} />
              <div className="h-px flex-1 bg-gradient-to-r from-[#659A9B]/15 via-[#659A9B]/40 to-[#659A9B]/15" />
            </div>
            <div className="text-[9px] font-semibold uppercase tracking-wider text-[#335E77] mt-1 whitespace-nowrap">{stops}</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-bold text-[#1B375B] tracking-tight leading-none">{route.to_airport}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[90px]">{toCity}</div>
          </div>
        </div>

        {/* Times row */}
        {deal.departure_time && deal.arrival_time && (
          <div className="flex items-center justify-between text-[11px] text-gray-600 px-1">
            <span className="font-semibold text-[#1B375B] tabular-nums">{deal.departure_time}</span>
            {deal.duration && <span className="text-gray-400">{deal.duration}</span>}
            <span className="font-semibold text-[#1B375B] tabular-nums">{deal.arrival_time}</span>
          </div>
        )}

        {/* Amenities micro-row */}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] text-gray-500 border-t border-dashed border-[#659A9B]/20 pt-2">
          {deal.checkin_baggage && (
            <span className="inline-flex items-center gap-1">
              <Briefcase className="w-3 h-3 text-[#659A9B]" />
              {deal.checkin_baggage}
            </span>
          )}
          {deal.meal_included && (
            <span className="inline-flex items-center gap-1">
              <Utensils className="w-3 h-3 text-[#659A9B]" />
              Meal
            </span>
          )}
          {deal.refundable && (
            <span className="inline-flex items-center gap-1">
              <RefreshCcw className="w-3 h-3 text-[#659A9B]" />
              Refundable
            </span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] text-gray-500">From</span>
          <span className="text-[22px] font-bold text-[#1B375B] tracking-tight leading-none tabular-nums">₹{fmtINR(lowestAdult)}</span>
          <span className="text-[10px] text-gray-500">/adult</span>
        </div>

        {/* Soonest departure / urgency banner */}
        {urgent ? (
          <div
            className="flex items-center gap-1.5 text-[11px] text-[#92400e] font-semibold bg-[#FEF3C7] border border-[#F5A623]/30 rounded-md px-2 py-1.5"
            data-testid="deal-urgency"
          >
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            Only {deal.soonest_seats} seat{deal.soonest_seats === 1 ? '' : 's'} left{soonest ? ` · ${soonest.full}${soonestReturn ? ` ⇆ ${soonestReturn.full}` : ''}` : ''}
          </div>
        ) : (
          soonest && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#335E77] bg-[#F5FBFB] border border-[#659A9B]/20 rounded-md px-2 py-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#659A9B] flex-shrink-0" />
              <span>
                {isRound ? 'Next cycle ' : 'Next departure '}
                <strong className="text-[#1B375B]">{soonest.full}</strong>
                {soonestReturn && <> ⇆ <strong className="text-[#1B375B]">{soonestReturn.full}</strong></>}
              </span>
            </div>
          )
        )}

        {/* Date chip strip — multi-departure deals only */}
        {showChips && (
          <div className="-mt-1" data-testid="deal-date-chips">
            <div className="text-[9px] uppercase tracking-wider text-gray-400 font-bold mb-1.5">
              {isRound ? 'Upcoming cycles' : 'Upcoming departures'}
            </div>
            <div className="flex flex-wrap gap-1">
              {deal.next_departures.map((d) => {
                const chip = formatChipDate(d.date);
                const retChip = d.return_date ? formatChipDate(d.return_date) : null;
                const lowSeats = (d.available_seats || 0) <= 5;
                return (
                  <button
                    key={d.date}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBook?.(deal, d.date);
                    }}
                    className={`px-1.5 py-1 rounded-md text-[10px] border transition-colors ${
                      lowSeats
                        ? 'border-[#F5A623]/45 bg-[#FFF8EC] text-[#92400e] hover:bg-[#FEF3C7]'
                        : 'border-[#659A9B]/25 bg-[#F5FBFB] text-[#1B375B] hover:bg-[#E7EEEE]'
                    }`}
                    data-testid={`deal-chip-${d.date}`}
                  >
                    <span className="font-semibold">
                      {chip.weekday} {chip.day}
                      {retChip && <> ⇆ {retChip.weekday} {retChip.day}</>}
                    </span>
                    <span className={`ml-1 ${lowSeats ? 'text-[#92400e]' : 'text-gray-500'}`}>· {d.available_seats}</span>
                  </button>
                );
              })}
              {deal.more_dates_count > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBook?.(deal);
                  }}
                  className="px-1.5 py-1 rounded-md text-[10px] border border-dashed border-[#659A9B]/35 bg-white text-[#335E77] hover:bg-[#F5FBFB] font-semibold"
                  data-testid="deal-chip-more"
                >
                  +{deal.more_dates_count} more
                </button>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={() => onBook?.(deal)}
          className="mt-auto w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#1B375B] hover:bg-[#0f2742] text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all"
          data-testid={`deal-book-btn-${deal.id}`}
        >
          Book Now
          <Plane className="w-3.5 h-3.5 rotate-45" />
        </button>
      </div>
    </div>
  );
};

export default SpecialDealCard;
